import React, { useState } from 'react';
import classnames from 'classnames';
import moment from 'moment';
import { useCountdown } from './hooks';
import { isAcceptedOrder, isPickedUpOrder } from 'shared/models/order';
import ContactModal from 'components/ContactModal';
import TextMessage from 'images/icons/text-message.svg?react-svg';
import Telephone from 'images/icons/telephone.svg?react-svg';
import Star from 'images/icons/stars/round-full.svg?react-svg';
import DefaultCourierAvatar from 'images/default-courier-avatar.png';
import { SuccessPill, AccentPill } from 'scy/Pill';
import { SecondaryButton } from 'scy/buttons';
import {
  textCenter,
  textLeft,
  tablet_textCenter,
  halfOpacity
} from 'scy/utils.mod.scss';
import {
  H2,
  SecondaryBody,
  SecondarySmallUppercase,
  AccentH5,
  AccentBody,
  Body,
  SecondaryH5
} from 'scy/text';
import { tablet_subtitle } from 'scy/text/styles.mod.scss';
import {
  flex,
  flexGrow1,
  centerItems,
  centerContent,
  spaceBetween
} from 'scy/flex.mod.scss';
import {
  hidden,
  tablet_block,
  tablet_inlineBlock,
  tablet_hidden
} from 'scy/display.mod.scss';
import padding from 'scy/padding.mod.scss';
import margin from 'scy/margin.mod.scss';
import styles from './ProgressSummary.mod.scss';

export default function OrderTrackingProgressSummary({
  contactNumber,
  order,
  shippingOption,
  trackingCode,
  trackingLink,
  hasLiveTracking
}) {
  return (
    <section
      className={classnames(
        textCenter,
        padding.top_md,
        padding.tablet_left_md,
        padding.tablet_right_md,
        padding.tablet_top_xl
      )}
    >
      <StatusInfo eta={order.eta} hasLiveTracking={hasLiveTracking} />

      {hasLiveTracking ? (
        <LiveProgress eta={order.eta} contactNumber={contactNumber} />
      ) : (
        <div
          className={`${padding.left_sm} ${padding.right_sm} ${
            padding.bottom_md
          }`}
        >
          {order.shipping ? (
            <ShippingProgress
              shippingOption={shippingOption}
              trackingCode={trackingCode}
              trackingLink={trackingLink}
            />
          ) : order.scheduled ? (
            <Scheduled
              time={order.scheduled}
              display={order.scheduled_display}
            />
          ) : (
            (isAcceptedOrder(order) || isPickedUpOrder(order)) && (
              <ETAWindow eta={order.eta} />
            )
          )}
          <Contact contactNumber={contactNumber} />
        </div>
      )}

      <CourierDetails courier={order.driver} />
    </section>
  );
}

function Contact({ contactNumber }) {
  const [showContactModal, setShowContactModal] = useState(false);

  return !contactNumber ? null : (
    <>
      <AccentBody className={classnames(flex, centerContent, tablet_hidden)}>
        <a className={styles.contactIcon} href={`sms:${contactNumber}`}>
          <TextMessage />
        </a>
        <a className={styles.contactIcon} href={`tel:${contactNumber}`}>
          <Telephone />
        </a>
      </AccentBody>

      <SecondaryButton
        className={classnames(hidden, tablet_inlineBlock, margin.xs)}
        onClick={() => setShowContactModal(true)}
      >
        Contact Saucey
      </SecondaryButton>

      <ContactModal
        isOpen={showContactModal}
        phoneNumber={contactNumber}
        handleClose={() => setShowContactModal(false)}
      />
    </>
  );
}

function StatusInfo({ eta, hasLiveTracking }) {
  const progress = (eta && eta.progress) || '';
  const progress_subtitle = (eta && eta.progress_subtitle) || '';

  return (
    <>
      {!!progress && (
        <H2
          tag="h1"
          marginClass={classnames(margin.top_0, margin.bottom_sm)}
          className={classnames(
            styles.heading,
            hasLiveTracking ? `${hidden} ${tablet_block}` : ''
          )}
        >
          {eta.progress}
        </H2>
      )}

      {!!progress_subtitle && (
        <SecondaryBody
          tag="p"
          marginClass={classnames(
            margin.top_0,
            margin.bottom_sm,
            margin.tablet_bottom_md
          )}
          className={classnames(
            tablet_subtitle,
            hasLiveTracking ? `${hidden} ${tablet_block}` : ''
          )}
        >
          {eta.progress_subtitle}
        </SecondaryBody>
      )}
    </>
  );
}

function LiveProgress({ eta, contactNumber }) {
  const remainingTime = useCountdown(eta);

  return (
    <div
      className={classnames(
        textCenter,
        flex,
        tablet_block,
        centerItems,
        padding.sm,
        padding.top_xs,
        padding.tablet_top_lg
      )}
    >
      {remainingTime != null && (
        <div className={styles.inProgressContainer}>
          <div
            className={classnames(
              styles.subheading,
              margin.right_xs,
              margin.tablet_bottom_xs
            )}
          >
            {`${remainingTime} ${remainingTime === 1 ? 'minute' : 'minutes'}`}
          </div>
          <SecondaryBody
            className={classnames(
              flexGrow1,
              textLeft,
              margin.right_xs,
              tablet_textCenter,
              margin.tablet_bottom_sm
            )}
          >
            est. arrival
          </SecondaryBody>
        </div>
      )}
      <Contact contactNumber={contactNumber} />
    </div>
  );
}

function CourierDetails({ courier }) {
  if (!courier) return null;

  const { first_name, last_name, avatar_url, rating } = courier;
  const name = `${first_name || ''}${
    last_name ? ` ${last_name.slice(0, 1)}.` : ''
  }`;
  const currentRating = (rating || 0).toFixed(1);

  return (
    <div className={styles.courierContainer}>
      <SecondaryH5
        uppercase
        className={classnames(halfOpacity, hidden, tablet_block)}
        marginClass={classnames(margin.top_0, margin.bottom_xs)}
      >
        Meet Your Courier
      </SecondaryH5>
      <div className={classnames(flex, centerItems, spaceBetween)}>
        {!!name && <div className={styles.courierName}>{name}</div>}
        {!!currentRating && (
          <Body className={classnames(flex, centerItems, centerContent)}>
            <span className={styles.star}>
              <Star width={18} height={17} />
            </span>
            <span className={margin.left_xxs}>{currentRating}</span>
          </Body>
        )}
        <img
          className={styles.courierAvatar}
          src={avatar_url ? avatar_url : DefaultCourierAvatar}
        />
      </div>
    </div>
  );
}

function ETAWindow({ eta }) {
  const range = (eta.window || '').replace('minutes', 'min.');
  return !range ? null : (
    <SuccessPill
      label="Arriving in"
      text={range}
      className={margin.bottom_sm}
    />
  );
}

function Scheduled({ time, display }) {
  if (!time || !display) return null;
  const day = moment(time).format('dddd, MMMM D');
  return <AccentPill label={day} text={display} className={margin.bottom_sm} />;
}

function ShippingProgress({ shippingOption, trackingCode, trackingLink }) {
  return (
    <>
      {trackingCode ? (
        <TrackingCode trackingCode={trackingCode} trackingLink={trackingLink} />
      ) : (
        !!shippingOption && (
          <AccentPill
            label="Shipping Option"
            text={shippingOption}
            className={margin.bottom_sm}
          />
        )
      )}
    </>
  );
}

function TrackingCode({ trackingCode, trackingLink }) {
  const Wrapper = ({ children }) =>
    trackingLink ? (
      <a href={trackingLink} rel="noreferrer noopener" target="_blank">
        {children}
      </a>
    ) : (
      children
    );

  return (
    <div className={margin.md}>
      <SecondarySmallUppercase tag="div">
        Track Your Order
      </SecondarySmallUppercase>
      <Wrapper>
        <AccentH5 tag="div">{trackingCode}</AccentH5>
      </Wrapper>
    </div>
  );
}
